// src/components/organisation/renderers/ServicesSectionRenderer.jsx
import React from 'react';
import { ArrowRight, ExternalLink, Tag } from 'lucide-react';

export const ServicesSectionRenderer = ({ 
  data, 
  bookData, 
  customStyling = {}, 
  onInteraction 
}) => {
  const {
    heading = 'Our Services',
    subHeading = '',
    services = [],
    layout = 'grid', // grid, list, cards
    enabled = true
  } = data || {};

  if (!enabled || !services?.length) return null;

  const trackInteraction = (action, details = {}) => {
    if (onInteraction) {
      onInteraction({
        type: 'services_interaction',
        action,
        section: 'services',
        sectionId: data?.id,
        ...details
      });
    }
  };

  const handleServiceClick = (service) => {
    trackInteraction('service_click', { serviceName: service.title });
  };

  return (
    <section className="services-section py-16 bg-white">
      <div className="container mx-auto px-4">
        {/* Section header */}
        {(heading || subHeading) && (
          <div className="text-center mb-16">
            {heading && (
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                {heading}
              </h2>
            )}
            {subHeading && (
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                {subHeading}
              </p>
            )}
          </div>
        )}

        {/* Services grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <div 
              key={index}
              className="service-card bg-white border border-gray-200 rounded-xl p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer group"
              onClick={() => handleServiceClick(service)}
            >
              {/* Service image */}
              {service.image && (
                <div className="mb-6 overflow-hidden rounded-lg">
                  <img 
                    src={service.image}
                    alt={service.title}
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
              )}
              
              {/* Service content */}
              <div>
                <div className="flex items-start justify-between mb-4">
                  <h3 className="text-xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors">
                    {service.title}
                  </h3>
                  {service.price && (
                    <span className="text-lg font-bold text-blue-600">
                      {service.currency || 'R'}{service.price}
                    </span>
                  )}
                </div>

                {service.category && (
                  <span className="inline-block bg-blue-100 text-blue-800 text-xs px-3 py-1 rounded-full mb-4">
                    {service.category}
                  </span>
                )}

                {service.description && (
                  <p className="text-gray-600 mb-6 line-clamp-3">
                    {service.description}
                  </p>
                )}

                {/* Service tags */}
                {service.tags && (
                  (() => {
                    // Ensure tags is an array
                    const tagsArray = Array.isArray(service.tags) 
                      ? service.tags 
                      : typeof service.tags === 'string' 
                        ? service.tags.split(',').map(t => t.trim()) 
                        : [];
                    
                    return tagsArray.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {tagsArray.slice(0, 3).map((tag, tagIndex) => (
                          <span 
                            key={tagIndex}
                            className="flex items-center gap-1 text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded"
                          >
                            <Tag className="h-3 w-3" />
                            {tag}
                          </span>
                        ))}
                        {tagsArray.length > 3 && (
                          <span className="text-xs text-gray-500">
                            +{tagsArray.length - 3} more
                          </span>
                        )}
                      </div>
                    );
                  })()
                )}

                {/* Learn more arrow */}
                <div className="flex items-center text-blue-600 font-medium group-hover:text-blue-700">
                  <span className="mr-2">Learn more</span>
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};